// "Cambiar clave" dentro del diálogo de Ajustes.
//
// Es una expansión IN-PLACE, no un diálogo apilado: dos backdrops en un
// teléfono dejan el segundo con 8px de aire lateral y el Escape cierra el
// equivocado. Además el drawer mobile no tiene dónde apilar.
import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

import PasswordFields from "../../views/authentication/PasswordFields";
import { fieldLabelSx, inputSx, primaryBtnSx } from "../../views/authentication/auth-styles";
import {
  adoptRotatedToken,
  changePassword,
  describeResetError,
  passwordRules,
} from "../../helpers/password-reset-api";
import { logout } from "../../helpers/authentication-helpers";

const ChangePasswordPanel = ({ email = "", onDone }) => {
  const [actual, setActual] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [estado, setEstado] = useState("idle"); // idle | saving | done
  const [error, setError] = useState(null);
  const [errorActual, setErrorActual] = useState(null);

  const puedeGuardar =
    actual.length > 0 &&
    password.length > 0 &&
    password === repeat &&
    passwordRules(password, email).every((r) => r.ok) &&
    estado !== "saving";

  const guardar = async (e) => {
    if (e) e.preventDefault();
    if (!puedeGuardar) return;
    setEstado("saving");
    setError(null);
    setErrorActual(null);
    try {
      const { data } = await changePassword(actual, password);
      // El backend rotó el token: las otras sesiones quedaron afuera y esta
      // recibe uno nuevo. Si por lo que sea no viene, hay que desloguear: el
      // cliente no maneja 401 globalmente, así que quedarse con el token viejo
      // deja la app cargada y TODO fallando en silencio.
      if (adoptRotatedToken(data)) {
        setEstado("done");
      } else {
        logout();
      }
    } catch (err) {
      const info = describeResetError(err);
      const detalleActual = err?.response?.data?.current_password?.[0];
      if (detalleActual) {
        setErrorActual(detalleActual);
      } else {
        setError(info);
      }
      setEstado("idle");
    }
  };

  if (estado === "done") {
    return (
      <Stack spacing={2} sx={{ py: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--color-success)" }}>
          Clave actualizada.
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.5 }}>
          Cerramos la sesión en tus otros aparatos. En este te quedas dentro.
        </Typography>
        <Button onClick={onDone} sx={{ textTransform: "none" }}>
          Volver a Ajustes
        </Button>
      </Stack>
    );
  }

  return (
    <Box component="form" noValidate onSubmit={guardar}>
      <Stack spacing={1.5}>
        <Box>
          <Typography component="label" htmlFor="current-password" sx={fieldLabelSx}>
            Clave actual
          </Typography>
          <Box
            component="input"
            id="current-password"
            name="current-password"
            type="password"
            autoComplete="current-password"
            value={actual}
            disabled={estado === "saving"}
            onChange={(e) => setActual(e.target.value)}
            sx={inputSx}
          />
          {errorActual ? (
            <Typography sx={{ fontSize: 11.5, color: "error.main", fontWeight: 600, mt: 0.6 }}>
              {errorActual}
            </Typography>
          ) : null}
        </Box>

        <PasswordFields
          password={password}
          repeat={repeat}
          onPasswordChange={setPassword}
          onRepeatChange={setRepeat}
          email={email}
          disabled={estado === "saving"}
        />

        <Button
          type="submit"
          variant="contained"
          disableElevation
          fullWidth
          sx={primaryBtnSx}
          disabled={!puedeGuardar}
        >
          {estado === "saving" ? "Guardando..." : "Guardar clave nueva"}
        </Button>

        {error ? (
          <Typography sx={{ fontSize: 12.5, color: "error.main", fontWeight: 600, textAlign: "center" }}>
            {error.message}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ChangePasswordPanel;
